import { Component, OnInit, OnDestroy, Output, EventEmitter, ViewChild, Input } from "@angular/core";
import { NgForm } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { Dependency } from "@fiduciaria/Shared/Types/dependency.interface";
import { Escrow } from "@fiduciaria/Shared/Types/escrow.interface";
import { NgSelectComponent } from "@ng-select/ng-select";
import { Subscription, Observable, map } from "rxjs";
// Servicios
import { Fideicomiso, NewUserFiduciary } from "src/app/Canales/Shared/Types/new-user";

import { UsuarioAdminCorporativo } from "@shared/Entities/AFI/Administracion/UsuarioCorporativo/usuarioAdminCorporativo.entity";
import { FiduciariaPermisosRole } from "@shared/Entities/Fiduciaria/FiduciariaPermisosRole.entity";
import { UsuariosRepository } from "@shared/Repositories/usuarios.repository";

import { NuevoFiduciariaService } from "../../../Shared/Services/nuevo-fiduciaria.service";

@Component({
  selector: "permisos-nuevo-fiduciaria",
  templateUrl: "./permisos.component.html",
  styleUrls: ["./permisos.component.scss"],
})
export class PermisosNuevoAdministradorFiduciariaComponent implements OnInit, OnDestroy {
  // Propiedades
  public subscriptions: Subscription[] = [];
  public isAdminCorporativo: boolean = false;
  public isUserCanales: boolean;
  private isFirstAssignment: boolean = true;
  public isOperativeCreation: boolean = false;

  // Datos de la interfaz
  public escrows: Escrow[] = [];
  public dependencies: Dependency[] = [];
  public fideicomiso: Fideicomiso[] = [];
  public permisosFiduciaria: FiduciariaPermisosRole[] = [];
  public permisosSeleccionados: number[] = [];
  public permisosIdAEliminar: number[] = [];
  public selectedFideicomiso: Fideicomiso;
  public selectedDependency: Dependency[] = [];
  public selectedAdmin: UsuarioAdminCorporativo;
  public currentUser: NewUserFiduciary;
  public identificationTypes$: Observable<UsuarioAdminCorporativo[]> = new Observable();

  // Flags de carga
  public loading: boolean = false;
  public loadingEscrows: boolean = false;
  public loadingDependency: boolean = false;
  public cargarAreas: boolean = false;
  public isEditing: boolean = false;

  private readonly EDIT_USER_ROUTE = "editar-usuario-fiduciaria";
  private readonly EDIT_USER_OPERATIONAL_ROUTE = "editar-usuario-fiduciaria/operational";
  private readonly CREATE_USER_OPERATIONAL_ROUTE = "create-fiduciary-user-canales/operational";

  // Input y Output
  @Input() public resetForm: Observable<boolean>;
  @Input() public editing: boolean;

  @Input() public set asignaciones(fideicomisosEdit: NewUserFiduciary | null) {
    this.currentUser = fideicomisosEdit;

    if (this.isFirstAssignment && this.currentUser) {
      this.selectedFideicomiso = this.currentUser.escrows;

      if (Array.isArray(this.selectedFideicomiso)) {
        this.fideicomiso.push(...this.selectedFideicomiso);
      } else {
        this.fideicomiso.push(this.selectedFideicomiso);
      }

      this.isFirstAssignment = false;
    }

    if (this.isUserCanales) {
      if (!this.isOperativeCreation && this.isEditing) {
        this.getDependencies();
        this.getEscrows();
      } else if (this.isOperativeCreation) {
        this.getUserFideicomisosByUserId(this.currentUser.idAdminSelected);
        this.getUsuariosAdminFiduciaria();

        this.identificationTypes$.subscribe((item) => {
          this.selectedAdmin = item.find((user) => user.id === this.currentUser.idAdminSelected);
        });
        this.adminSelect.select({ value: this.selectedAdmin });
      }
    }
    this.onFormReset();
  }

  @Output() public back: EventEmitter<void> = new EventEmitter();
  @Output() public submitForm: EventEmitter<Fideicomiso[]> = new EventEmitter();

  // Referencias a elementos del DOM
  @ViewChild("formNuevaCompania", { static: true }) public formNuevaCompania: NgForm;
  @ViewChild("escrowSelect", { static: true }) public escrowSelect: NgSelectComponent;
  @ViewChild("adminSelect", { static: true }) public adminSelect: NgSelectComponent;
  @ViewChild("dependenciesSelect", { static: true }) public dependenciesSelect: NgSelectComponent;

  // Constructor
  constructor(
    private readonly formularioService: NuevoFiduciariaService,
    private readonly userRepository: UsuariosRepository,
    private readonly router: Router,
    private readonly route: ActivatedRoute
  ) {}

  // Ciclo de vida: ngOnInit
  public ngOnInit(): void {
    this.resetForm.subscribe((formReset) => {
      if (formReset) {
        this.onFormReset();
      }
    });

    this.isEditing = this.router.url.includes(this.EDIT_USER_ROUTE);
    this.isOperativeCreation = this.isEditing
      ? this.router.url.includes(this.EDIT_USER_OPERATIONAL_ROUTE)
      : this.router.url.includes(this.CREATE_USER_OPERATIONAL_ROUTE);

    if (!this.isEditing) {
      this.getFiduciariaPermisosRole();
    } else {
      const guiId = this.route.snapshot.params.id;
      this.getPermisosByUserId(guiId);
    }

    this.userRepository.getInfoClienteActual().subscribe((usuario) => {
      // Debe ser canales Fiduciaria, el rol
      this.isUserCanales = usuario.usuarioRole.some((x) => x.role.nombre === "Canales Fiduciaria");
      if (!this.isUserCanales && this.isOperativeCreation) {
        this.getUserFideicomisos();
      }

      if (this.isUserCanales && !this.isEditing && !this.isOperativeCreation) {
        this.getDependencies();
        this.getEscrows();
        this.getUsuariosAdminFiduciaria();
      }
      if (this.isUserCanales && !this.isEditing && this.isOperativeCreation) {
        this.getUsuariosAdminFiduciaria();
      }
    });
  }

  // Ciclo de vida: ngOnDestroy
  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      if (!subscription.closed) subscription.unsubscribe();
    });
  }

  // Métodos públicos
  public asignarFideicomiso(): void {
    this.cargarAreas = false;
    const existingFideicomiso = this.fideicomiso.find((escrow) => escrow.sid === this.selectedFideicomiso.sid);
    if (!existingFideicomiso) {
      this.addNewFideicomiso();
    }

    this.escrowSelect.handleClearClick();
    this.dependenciesSelect.handleClearClick();
  }

  public guardarCambios(): void {
    this.fideicomiso.forEach((item) => {
      item.permisosId = this.permisosSeleccionados;
      item.permisosIdAEliminar = this.permisosIdAEliminar;
      item.userId = this.isOperativeCreation && this.isUserCanales ? this.selectedAdmin.id : 0;
      item.isAdmin = !this.isOperativeCreation;
    });

    this.submitForm.emit(this.fideicomiso);
  }

  public eliminarDependency(deletedEscrow: Fideicomiso, deleteDependency: Dependency): void {
    if (!deletedEscrow || !this.fideicomiso.length) return;

    const escrowIndex = this.fideicomiso.findIndex((escrow) => escrow.sCodigo === deletedEscrow.sCodigo);
    if (escrowIndex !== -1) {
      const dependencyIndex = this.fideicomiso[escrowIndex].dependencies.findIndex(
        (dep) => dep.sCodigo === deleteDependency.sCodigo
      );
      if (dependencyIndex !== -1) {
        this.fideicomiso[escrowIndex].dependencies.splice(dependencyIndex, 1);
      }

      if (this.fideicomiso[escrowIndex].dependencies.length === 0) {
        this.fideicomiso.splice(escrowIndex, 1);
      }
    }
  }

  public onFormReset(): void {
    if (this.formNuevaCompania) this.formNuevaCompania.resetForm();
  }

  public onAdminChanged(id: number): void {
    this.escrowSelect.handleClearClick();
    this.fideicomiso = [];
    if (!id) return;

    this.getUserFideicomisosByUserId(id);

    if (this.dependenciesSelect) {
      this.dependenciesSelect.handleClearClick();
    }
  }

  private getUserFideicomisosByUserId(userId: number): void {
    this.escrows = [];
    this.formularioService.getUserFideicomisosByUserId(userId).subscribe((escrows) => {
      this.loadingEscrows = false;
      this.escrows = escrows;
    });
  }

  public onEscrowChanged(id: number): void {
    this.cargarAreas = false;
    this.dependenciesSelect.handleClearClick();
    if (!id) return;

    this.getAreasByIdFideicomiso(id);

    if (this.dependenciesSelect) {
      this.dependenciesSelect.handleClearClick();
    }
  }

  private getAreasByIdFideicomiso(id: number): void {
    this.formularioService.getAreasByIdFideicomiso(id).subscribe((areasByFideicomisos) => {
      this.loadingEscrows = false;
      this.dependencies = areasByFideicomisos;
    });
  }

  public onPermissionChange(permiso: FiduciariaPermisosRole): void {
    if (this.isEditing) {
      if (!permiso.checked) {
        const indexToRemove = this.permisosIdAEliminar.indexOf(permiso.idRole);
        if (indexToRemove !== -1) {
          this.permisosIdAEliminar.splice(indexToRemove, 1);
        }
        this.permisosSeleccionados.push(permiso.idRole);
      } else {
        const indexToRemove = this.permisosSeleccionados.indexOf(permiso.idRole);
        if (indexToRemove !== -1) {
          this.permisosSeleccionados.splice(indexToRemove, 1);
        }
        this.permisosIdAEliminar.push(permiso.idRole);
      }
    } else {
      const index = this.permisosSeleccionados.indexOf(permiso.idRole);
      if (!permiso.checked) {
        this.permisosSeleccionados.push(permiso.idRole);
      } else if (permiso.checked && index !== -1) {
        this.permisosSeleccionados.splice(index, 1);
      }
    }
  }

  // Métodos privados
  private addNewFideicomiso(): void {
    this.selectedFideicomiso.dependencies = this.selectedDependency;
    this.fideicomiso.push(this.selectedFideicomiso);
  }

  private getUserFideicomisos(): void {
    this.escrows = [];
    this.loadingEscrows = true;
    this.formularioService.getUserFideicomisos().subscribe((fideicomisos) => {
      this.loadingEscrows = false;
      this.escrows = fideicomisos;
    });
  }

  public onSwitchChange(event: boolean): void {
    this.cargarAreas = event;
    this.selectedDependency = [];

    if (this.cargarAreas) {
      this.selectedDependency = this.dependencies;
      this.selectedDependency.forEach((element) => {
        this.dependenciesSelect.select({
          value: element,
          label: element.sNombre,
        });
      });
    }
  }

  private getEscrows(): void {
    this.escrows = [];
    this.loadingEscrows = true;
    this.formularioService.getEscrows().subscribe((escrow) => {
      this.loadingEscrows = false;
      this.escrows = escrow;
    });
  }

  private getDependencies(): void {
    this.dependencies = [];
    this.loadingDependency = true;
    this.formularioService.getDependencies().subscribe((dependency) => {
      this.loadingDependency = false;
      this.dependencies = dependency;

      if (this.dependencies.length === 1) {
        this.selectedDependency = this.dependencies;
        this.onEscrowChanged(0);
      }
    });
  }

  private getFiduciariaPermisosRole(): void {
    this.loadingEscrows = true;
    this.permisosFiduciaria = [];

    this.formularioService.getFiduciariaPermisosRole().subscribe((permisos) => {
      const permisosTemp: FiduciariaPermisosRole[] = [];
      permisos.forEach((permiso) => {
        this.permisosSeleccionados.push(permiso.idRole);
        permisosTemp.push({ ...permiso, checked: true });
      });
      this.permisosFiduciaria = permisosTemp;
      this.loadingEscrows = false;
    });
  }

  private getPermisosByUserId(guiId: string): void {
    this.loading = true;
    this.permisosFiduciaria = [];

    this.formularioService.getPermisosByUserId(guiId).subscribe((permisos: FiduciariaPermisosRole[]) => {
      this.permisosFiduciaria = permisos;
      permisos.forEach((permiso) => {
        if (permiso.checked) {
          this.permisosSeleccionados.push(permiso.idRole);
        }
      });
      this.loading = false;
    });
  }

  private getUsuariosAdminFiduciaria(): void {
    this.identificationTypes$ = this.formularioService.getUsuariosAdminFiduciaria().pipe(
      map((items) =>
        items.map((item) => ({
          ...item,
          displayLabel: `(${item.userProperties.userCode || item.email}) ${item.nombres} ${item.apellidos} `,
        }))
      )
    );
  }
}
